import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { XIcon as CloseIcon } from 'lucide-react'

import Colors from 'colors.json'
import { InputField } from 'components/InputField'
import Dropdown from 'components/Dropdown'
import Button from 'components/Button'
import { DropDownItem } from 'types/components'
import {
  Report13CreateFormSchemas,
  Report13CSVDataSchemas,
  Report13CreateFormTypes,
  Report13CSVDataTypes,
} from 'schemas/reports-#-13-schemas'

import {
  useReportMerchantsMutation,
  useReportMerchantsSelectQuery,
} from './use-report-merchants-lookup'
import { TRANSACTION_TYPES, TRANSACTION_REPORT_TYPE, TRANSACTION_REPORT_INCLUDE_TRADER } from 'constants/filters'
import { ROUTES } from 'constants/routing'
import { useReport13Mutation } from './use-report-#-13'
import { ExportToCSV } from '../../utils-reports-actions'

export const CreateReport13Form = ({
  onCloseModal,
}: {
  onCloseModal: () => void
}) => {
  const resultRef = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  const [queryString, queryStringSet] = useState<string | null>(null)
  const { onSearchMerchantsLoader } = useReportMerchantsMutation()
  const { selectedMerchants, onSelectMerchants } =
    useReportMerchantsSelectQuery()
  const [isAllTraders, isAllTradersSet] = useState(false)

  const date = new Date().toISOString().split('T')[0]
  const formDefaultValues: Report13CreateFormTypes = {
    includeAllTrader: 1,
    transactionTypeId: null,
    startDate: date,
    endDate: date,
    fromTraderIds: [],
    reportTypeId: '2',
  }

  const form = useForm<Report13CreateFormTypes>({
    resolver: zodResolver(Report13CreateFormSchemas),
    defaultValues: formDefaultValues,
  })

  const {
    data: report13Data,
    mutateAsync: postReport13Mutation,
    isSuccess,
    isError,
    isLoading,
  } = useReport13Mutation()

  const handleView = useCallback(() => {
    navigate(
      ROUTES.ReportsView.path +
      `/?report-query=${queryString}&report-type=report_13`
    )
  }, [queryString, navigate])

  const onSubmit = useCallback(
    async (formData: Report13CreateFormTypes) => {
      const queryString = encodeURIComponent(JSON.stringify(formData))
      queryStringSet(queryString)
      postReport13Mutation(formData)
    },
    [postReport13Mutation, queryStringSet]
  )

  useEffect(() => {
    form.setValue(
      'fromTraderIds',
      selectedMerchants.map((item) => Number(item.value))
    )
  }, [form, selectedMerchants])

  useEffect(() => {
    if (resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isSuccess, isError, resultRef])

  useEffect(() => {
    isAllTradersSet(form.watch('includeAllTrader') === 1)
  }, [form.watch('includeAllTrader')])

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="mb-4">
        <Controller
          control={form.control}
          name="includeAllTrader"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              title="Merchant Selection"
              options={TRANSACTION_REPORT_INCLUDE_TRADER}
              defaultValue={TRANSACTION_REPORT_INCLUDE_TRADER.find(
                (i) => i.value === String(value)
              )}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
                form.setValue(
                  'fromTraderIds',
                  option.value === '1' ? [] : selectedMerchants.map((m) => Number(m.value))
                )
                isAllTradersSet(option.value === '1')
              }}
              className="w-full"
            />
          )}
        />
      </div>

      {!isAllTraders ? (
        <div className="mb-4">
          <Controller
            name="fromTraderIds"
            control={form.control}
            render={({ field }) => (
              <Dropdown
                title="Lookup Merchants"
                loadOptions={onSearchMerchantsLoader}
                onChange={(options: DropDownItem[]) => {
                  options.forEach((option) => onSelectMerchants(option))
                  field.onChange(options.map((m) => Number(m.value)))
                }}
                placeholder="Search merchants ..."
                className="w-full tracking-wider"
                borderColor="#C4C4C4"
                containerClassName="focus:border-primary"
                isClearable
                isSearchable
                async
              />
            )}
          />
        </div>
      ) : null}

      {!isAllTraders && selectedMerchants.length ? (
        <div className="mb-4">
          <p className="font-light tracking-wide">Selected Merchants</p>
          <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
            {selectedMerchants.map((item) => (
              <span
                key={item.value}
                onClick={() => onSelectMerchants(item)}
                className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
              >
                {item.label}
                <CloseIcon className="w-3 h-3" />
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mb-4">
        <Controller
          control={form.control}
          name="transactionTypeId"
          render={({ field: { onChange } }) => (
            <Dropdown
              title="Transaction Type"
              options={TRANSACTION_TYPES}
              onSelect={(option: DropDownItem) => {
                onChange(option.value === '0' ? null : Number(option.value))
              }}
              className="w-full"
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="reportTypeId"
          render={({ field: { onChange } }) => (
            <Dropdown
              title="Filter"
              options={TRANSACTION_REPORT_TYPE}
              onSelect={(option: DropDownItem) => {
                onChange(option.value)
              }}
              className="w-full"
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="date"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
              error={form.formState.errors.startDate?.message}
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="date"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
              error={form.formState.errors.endDate?.message}
            />
          )}
        />
      </div>

      <p className='font-bold text-xs mt-[28px]'>*This report only displays a maximum of 1000 records</p>

      <div className="flex gap-4 justify-end pb-4 pl-4 pr-4">
        <Button
          type="submit"
          className="mr-13 rounded bg-primary"
          disabled={isLoading}
        >
          <span className="tracking-wider font-bold">
            {isLoading ? 'Submitting ...' : 'Submit'}
          </span>
        </Button>

        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          <span className="tracking-wider font-bold">Cancel</span>
        </Button>
      </div>

      {isError || (isSuccess && !report13Data?.data.length) ? (
        <div
          ref={resultRef}
          className="flex flex-col gap-4 bg-gray-1300 p-4 rounded-md"
        >
          <div>
            <p className="tracking-wide text-red font-light">
              {`Sorry, there are no items available.`}
            </p>
          </div>
        </div>
      ) : null}

      {isSuccess && report13Data?.data.length ? (() => {
        const csvKeys = Object.keys(Report13CSVDataSchemas.shape)
        const csvData = report13Data.data.map((row: Report13CSVDataTypes) =>
          Object.fromEntries(
            csvKeys.map((key) => [key, row[key as keyof Report13CSVDataTypes]])
          )
        )
        return (
          <div
            ref={resultRef}
            className="flex flex-col gap-4 bg-gray-1300 p-4 rounded-md"
          >
            <div>
              <p className="font-bold text-lg tracking-wide text-primary">
                Results:
              </p>
              <p className="tracking-wide text-primary font-light">
                {`There are ${report13Data.data.length} records available in the report
                ready for download.`}
              </p>
            </div>

            <Button
              type="button"
              className="ml-1 rounded-lg tracking-wide font-bold"
              iconRight={false}
              onClick={() =>
                ExportToCSV(csvData, { filename: 'Report-13' })
              }
            >
              Download CSV Report
            </Button>

            <Button
              type="button"
              className="ml-1 rounded-lg tracking-wide font-bold"
              iconRight={false}
              onClick={handleView}
            >
              View Report
            </Button>
          </div>
        )
      })() : null}
    </form>
  )
}
